import React, { useState, useContext } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ThemeContext } from '../contexts/ThemeContext';

const Navbar = ({ onLogout, userRole = 'student', onAdminSidebarToggle }) => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { darkMode } = useContext(ThemeContext);
  
  const toggleMenu = () => {
    if (userRole === 'admin_user' && onAdminSidebarToggle) {
      onAdminSidebarToggle();
    } else {
      setIsMenuOpen(!isMenuOpen);
    }
  };
  
  // Close mobile menu when user clicks a link
  const closeMenu = () => {
    setIsMenuOpen(false);
  };
  
  // Get navigation items based on user role
  const getNavLinks = (role) => {
    switch (role) {
      case 'admin':
        return [];
      case 'teacher':
        // Get teacher's class_type from localStorage to determine if they are BK
        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        const classType = userData.class_type || 'praktek';
        const isGuruBk = classType === 'bk';
        const isWaliKelas = !!userData.headOfClass;
        
        const teacherLinks = [
          { path: '/teacher', label: 'Dashboard' },
          { path: '/teacher/lapor-kts', label: 'Lapor KTS' },
          { path: '/teacher/status-siswa', label: 'Status Siswa' },
          { path: '/teacher/rekap-laporan', label: 'Rekap Laporan' },
        ];

        if (isWaliKelas) {
          teacherLinks.push({ path: '/teacher/wali-kelas-monitoring', label: 'Wali Kelas Monitoring' });
        }
        
        // Only add Konseling menu for BK teachers
        if (isGuruBk) {
          teacherLinks.push({ path: '/teacher/konseling', label: 'Konseling' });
        }
        
        return teacherLinks;
      case 'student':
        return [
          { path: '/home', label: 'Dashboard' },
          { path: '/profil', label: 'Profil' },
          { path: '/surat-ijin', label: 'Ijin' },
          { path: '/laporan', label: 'Laporan' },
          { path: '/pelayanan', label: 'Pelayanan' }
        ];
      default:
        return [];
    }
  };
  
  return (
    <NavbarContainer>
      <NavbarContent>
        <LogoContainer onClick={() => navigate(userRole === 'student' ? '/home' : userRole === 'teacher' ? '/teacher' : '/admin')}>
          <LogoImage src="/images/logo.png" alt="SIMMICO Logo" />
        </LogoContainer>
        
        <MenuToggle onClick={toggleMenu} aria-label={userRole === 'admin_user' ? 'Toggle admin sidebar' : 'Toggle menu'} $isOpen={isMenuOpen}>
          <span />
          <span />
          <span />
        </MenuToggle>
        
        <StyledNavMenu $isOpen={isMenuOpen}>
          {getNavLinks(userRole).map((link, index) => (
            <NavItem key={index}>
              <StyledNavLink to={link.path} onClick={closeMenu}>
                {link.label}
              </StyledNavLink>
            </NavItem>
          ))}
        </StyledNavMenu>
        
        <UserActions>
          <ActionIcon onClick={() => {
            closeMenu();
            navigate('/settings');
          }}>
            <span>⚙️</span>
          </ActionIcon>
          <LogoutButton onClick={() => {
            closeMenu();
            onLogout();
          }}>Logout</LogoutButton>
        </UserActions>
      </NavbarContent>
    </NavbarContainer>
  );
};

const NavbarContainer = styled.nav`
  background-color: var(--background);
  box-shadow: var(--box-shadow);
  position: sticky;
  top: 0;
  z-index: 100;
  transition: background-color 0.3s, box-shadow 0.3s;
  overflow: visible;
`;

const NavbarContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1.5rem;
  max-width: 1280px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 0.75rem 0.5rem;
    position: relative;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const LogoImage = styled.img`
  height: 40px;
  max-width: 100%;
  object-fit: contain;
  
  @media (max-width: 768px) {
    height: 30px;
  }
`;

const MenuToggle = styled.div`
  display: none;
  flex-direction: column;
  gap: 4px;
  cursor: pointer;
  
  span {
    display: block;
    width: 25px;
    height: 3px;
    background-color: var(--text-dark);
    border-radius: 2px;
    transition: all 0.3s;
    transform-origin: 1px;
  }
  
  // Animate hamburger icon to an "X" when the menu is open
  ${({ $isOpen }) => $isOpen && `
    span:nth-child(1) {
      transform: rotate(45deg);
    }
    span:nth-child(2) {
      opacity: 0;
      transform: translateX(20px);
    }
    span:nth-child(3) {
      transform: rotate(-45deg);
    }
  `}
  
  @media (max-width: 768px) {
    display: flex;
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    z-index: 10;
  }
`;

const StyledNavMenu = styled.ul`
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  
  @media (max-width: 768px) {
    flex-direction: column;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background-color: var(--background);
    box-shadow: var(--box-shadow);
    padding: 1rem 0;
    display: ${props => props.$isOpen ? 'flex' : 'none'};
    transition: background-color 0.3s;
    z-index: 1000;
  }
`;

const NavItem = styled.li`
  margin: 0 0.5rem;
  
  @media (max-width: 768px) {
    margin: 0;
  }
`;

const StyledNavLink = styled(NavLink)`
  display: block;
  padding: 0.5rem 1rem;
  color: var(--text-dark);
  text-decoration: none;
  font-weight: 500;
  border-radius: 8px;
  transition: all 0.2s;
  
  &:hover {
    background-color: var(--background-light);
  }
  
  &.active {
    color: var(--primary);
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem 1.5rem;
    border-radius: 0;
    
    &:hover, &.active {
      background-color: var(--background-light);
    }
  }
`;

const UserActions = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  
  @media (max-width: 768px) {
    gap: 0.3rem;
  }
`;

const ActionIcon = styled.div`
  position: relative;
  cursor: pointer;
  padding: 0.5rem;
  font-size: 1.25rem;
  transition: transform 0.2s;
  
  &:hover {
    transform: scale(1.1);
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem 0.25rem;
    font-size: 1.1rem;
  }
`;

const LogoutButton = styled.button`
  background-color: transparent;
  color: var(--text-dark);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: var(--background-light);
  }
  
  @media (max-width: 768px) {
    padding: 0.4rem 0.6rem;
    font-size: 0.75rem;
  }
`;

export default Navbar;